<template>
<div class="accordion-select" @click="handleClick">
    <v-select :options="columns" :modelValue="value_obj" @update:modelValue="handleModelUpdate"
        :searchable="true" class="searchable-select column-display category"
        :selectable="(option) => option.text != ''"
        label="text" @search="fetchOptions"
        :filterable="false"
        :clear-on-select="false"
        @open="handleColumnDisplayOpen"
        :disabled="!if_allow_edit"
        :placeholder="placeholder"
        >
        <template #selected-option="{ text, category, subcategory }">
            <span :option-selected="text" 
                  data-bs-toggle="tooltip"
                  :data-bs-original-title="getColumnTitle(value_obj)"> 
                <i v-if="value_obj && value_obj.icon_class" :class="value_obj.icon_class" class="me-2"></i>
                {{(category != null && category != "" && category != "list-header" ? category + ": " : "")}}
                {{subcategory != null && subcategory != "" ? subcategory + " › " : ""}}
                {{text}}
            </span>
        </template>

        <template #open-indicator="{ attributes }">
          <span v-bind="attributes" style="width: 12px; line-height: 8px;"><svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'><path fill='none' stroke='#343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/></svg></span>
        </template>

        <template #option="{ text, category, subcategory, extra, icon_class }">
          <div v-if="text == '' && !subcategory" :data-category="category" class="category-header"
            @click="expandCategory(category, null)"
            @mouseover="handleHover($event)"
            data-isexpanded="false">
            {{ category }} <i class="fas fa-caret-right"></i>
          </div>
          <div v-else-if="text == '' && subcategory"
            :data-subcategory="category"
            :data-subcategory-name="subcategory"
            class="subcategory-header"
            @click="expandSubcategory(category, subcategory, null)"
            @mouseover="handleHover($event)"
            data-isexpanded="false"
            v-show="isSubcategoryVisible(category, subcategory)">
            {{ subcategory }} <i class="fas fa-caret-right"></i>
          </div>
          <div v-else class="suboption" :data-subcategory="category" :data-subcategory-name="subcategory"
            :class="shouldShowOption(category, subcategory)"
            @mouseover="handleHover($event)">
            <i v-if="icon_class" :class="icon_class" class="me-2"></i>{{getDisplayText(text)}} <span v-html="extra" v-if='extra != null'></span>
          </div>
        </template>
        <template #no-options="{ search, searching, loading }">
            <div class="suboption show" v-if="is_loading">
                <div class="d-inline-block spinner-border spinner-border-sm ms-2" role="status"> <span class="visually-hidden">Loading...</span></div>  Loading columns
            </div>
            <div class="suboption show" v-else>
                <em>No results found</em>
            </div>
        </template>
    </v-select>
    <input type="hidden" :name="name" :value="(value_obj == null ? null : value_obj.value)" />
</div>
</template>

<script>
    import vSelect from "vue3-select";

    export default {
        props: {
            modelValue: {
              default: '',
            },
            options: {
                type: [Array, String],
                default: () => [],
                validator: function(value) {
                    return Array.isArray(value) || typeof value === 'string';
                }
            },
            name: {
              type: String,
              default: 'vue-select',
            },
            if_allow_edit: {
                type: Boolean,
                default: true
            },
            splitText: {
                type: Boolean,
                default: true
            },
            placeholder: {
                type: String,
                default: null
            }
        },
        components: {
            vSelect
        },
        data() {
            return {
                value_obj: null,
                columns: [],
                orig_columns: [],
                expanded_categories: [],
                expanded_subcategories: [],
                is_loading: false
            };
        },
        beforeMount() {
            // Initialize columns with empty array if options is not valid
            if (!this.options || typeof this.options === 'string' || !Array.isArray(this.options)) {
                this.columns = [];
                this.orig_columns = [];
            } else {
                this.processOptions(this.options);
            }
            this.resetValueObject();
        },
        watch:{
            modelValue: {
                immediate: true,
                handler(newValue) {
                    this.$nextTick(() => {
                        this.resetValueObject();
                    });
                }
            },
            options: {
                immediate: true,
                handler(newOptions) {
                    this.$nextTick(() => {
                        this.processOptions(newOptions);
                    });
                }
            },
            value_obj: {
                handler(newVal) {
                    // Reinitialize tooltips when value changes
                    this.$nextTick(() => {
                        this.initTooltips();
                    });
                },
                deep: true
            }
        },
        mounted() {
            this.processOptions(this.options);
        },
        updated() {
            this.initTooltips();
        },
        methods: {
            compareValues(a, b) {
                // Helper function to check if a string looks like a number (including formatted currency)
                const isNumeric = (str) => {
                    if (!str || typeof str !== 'string') return false;
                    
                    // Remove common currency symbols and formatting
                    const cleaned = str.replace(/[$,\s]/g, '');
                    
                    // Check if it's a valid number (including decimals and negative numbers)
                    return /^-?\d+(\.\d+)?$/.test(cleaned);
                };
                
                // Helper function to extract numeric value for comparison
                const getNumericValue = (str) => {
                    if (!str || typeof str !== 'string') return 0;
                    
                    // Remove currency symbols, commas, and spaces
                    const cleaned = str.replace(/[$,\s]/g, '');
                    
                    // Parse as float
                    const num = parseFloat(cleaned);
                    return isNaN(num) ? 0 : num;
                };
                
                // If both values look like numbers, compare numerically
                if (isNumeric(a) && isNumeric(b)) {
                    const aNum = getNumericValue(a);
                    const bNum = getNumericValue(b);
                    return aNum - bNum;
                }
                
                // If only one looks like a number, put the number first
                if (isNumeric(a) && !isNumeric(b)) return -1;
                if (!isNumeric(a) && isNumeric(b)) return 1;
                
                // Otherwise, use locale-aware string comparison
                return a.localeCompare(b);
            },
            handleColumnDisplayOpen() {
                // Reset expanded states
                this.expanded_categories = [];
                this.expanded_subcategories = [];

                // First handle the selected column's category/subcategory
                if (this.value_obj && this.value_obj.category) {
                    // Don't expand the selected category initially
                    this.expanded_categories = [];
                    if (this.value_obj.subcategory) {
                        this.expanded_subcategories = [];
                    }
                }

                // Ensure all categories and subcategories are collapsed
                this.$nextTick(() => {
                    // Find all category headers and collapse them
                    const categoryHeaders = document.querySelectorAll('.category-header');
                    categoryHeaders.forEach(header => {
                        const category = header.dataset.category;
                        if (category) {
                            const icon = header.querySelector('.fa-caret-right, .fa-caret-down');
                            if (icon) {
                                icon.classList.add('fa-caret-right');
                                icon.classList.remove('fa-caret-down');
                            }
                            header.dataset.isexpanded = "false";
                        }
                    });

                    // Find all subcategory headers and collapse them
                    const subcategoryHeaders = document.querySelectorAll('.subcategory-header');
                    subcategoryHeaders.forEach(header => {
                        const icon = header.querySelector('.fa-caret-right, .fa-caret-down');
                        if (icon) {
                            icon.classList.add('fa-caret-right');
                            icon.classList.remove('fa-caret-down');
                        }
                        header.dataset.isexpanded = "false";
                    });
                });
            },
            getColumnTitle(col) {
                if(col == null || col.value == null)
                    return '';
                
                const { category, subcategory, text } = col;
                let title = '';
                
                if(category != null && category != "") {
                    title += category + ": ";
                }
                if(subcategory != null && subcategory != "") {
                    title += subcategory + " › ";
                }
                title += text || '';
                
                return title;
            },
            resetValueObject(){
                if(this.modelValue != null) {
                    if (typeof this.modelValue === 'object' && this.modelValue.value !== undefined) {
                        // If modelValue is already an object with a value property, use it directly
                        this.value_obj = {
                            value: this.modelValue.value,
                            text: this.modelValue.text,
                            category: this.modelValue.category,
                            subcategory: this.modelValue.subcategory,
                            icon_class: this.modelValue.icon_class,
                            type: this.modelValue.type
                        };
                    } else if (Array.isArray(this.options)) {
                        // Try to find the option by value only if options is an array
                        let foundOption = null;
                        if (this.modelValue.id) {
                            foundOption = this.options.find(option => option.value === this.modelValue.id.toString());
                        } else {
                            foundOption = this.options.find(option => option.value === this.modelValue);
                        }
                        if (foundOption) {
                            this.value_obj = foundOption;
                        } else {
                            // Default to empty
                            this.value_obj = {
                                value: '',
                                category: '',
                                subcategory: '',
                                text: "",
                                icon_class: ""
                            };
                        }
                    } else {
                        // Default to empty if options is not an array
                        this.value_obj = {
                            value: '',
                            category: '',
                            subcategory: '',
                            text: "",
                            icon_class: ""
                        };
                    }
                } else {
                    // Default to empty
                    this.value_obj = {
                        value: '',
                        category: '',
                        subcategory: '',
                        text: "",
                        icon_class: ""
                    };
                }
            },
            handleClick() {
              this.$emit('editing');
            },
            handleModelUpdate(value) {
                this.value_obj = value;
                this.emitInput();
            },
            emitInput() {
                if(this.value_obj == null) {
                    this.$emit('update:modelValue', null);
                } else {
                    // Ensure we're emitting a clean object without Vue's reactivity
                    const cleanValue = {
                        value: this.value_obj.value,
                        text: this.value_obj.text,
                        category: this.value_obj.category,
                        subcategory: this.value_obj.subcategory,
                        icon_class: this.value_obj.icon_class, // Add icon_class for asset_type
                        type: this.value_obj.type !== undefined ? this.value_obj.type : null // Add type for paid_by field, allow null if missing
                    };
                    this.$emit('update:modelValue', cleanValue);
                }
            },
            addHeaders() {
                var current_category = null;
                var current_subcategory = null;

                // Sort columns to ensure proper nesting
                this.columns.sort((a, b) => {
                    // Convert values to strings for comparison
                    const aValue = String(a.value);
                    const bValue = String(b.value);
                    
                    // First sort by category (either from dot notation or category property)
                    const aCategory = a.category || (aValue.indexOf(".") != -1 ? aValue.split(".")[0] : null);
                    const bCategory = b.category || (bValue.indexOf(".") != -1 ? bValue.split(".")[0] : null);
                    
                    // If both have categories, sort by category first
                    if (aCategory && bCategory) {
                        const categoryCompare = this.compareValues(aCategory, bCategory);
                        if (categoryCompare !== 0) return categoryCompare;
                    } else if (aCategory && !bCategory) {
                        return -1;
                    } else if (!aCategory && bCategory) {
                        return 1;
                    }
                    
                    // If same category or no categories, sort by dot notation
                    if(aValue.indexOf(".") == -1 && bValue.indexOf(".") != -1)
                        return -1;
                    else if(aValue.indexOf(".") != -1 && bValue.indexOf(".") == -1)
                        return 1;
                    
                    // If same category, sort by subcategory presence
                    if(aValue.indexOf(".") != -1 && bValue.indexOf(".") != -1) {
                        const aParts = aValue.split(".");
                        const bParts = bValue.split(".");
                        
                        // If same category
                        if(aParts[0] === bParts[0]) {
                            // If one has subcategory and other doesn't, put the one without subcategory first
                            if(aParts.length === 2 && bParts.length > 2) return -1;
                            if(aParts.length > 2 && bParts.length === 2) return 1;
                            
                            // If both have subcategories, sort by subcategory
                            if(aParts.length > 2 && bParts.length > 2) {
                                return this.compareValues(aParts[1], bParts[1]);
                            }
                        }
                        return this.compareValues(aParts[0], bParts[0]);
                    }
                    
                    return this.compareValues(aValue, bValue);
                });

                for(let i = 0; i < this.columns.length; i++) {
                    let col = this.columns[i];
                    let category = null;
                    let subcategory = null;
                    let text = col.text;

                    // Convert value to string for processing
                    const colValue = String(col.value);

                    // Special handling for CASE WHEN statements - use the category property and text
                    if(colValue.indexOf("CASE WHEN") != -1 && col.category && col.category.trim() != '') {
                        category = this.propercase(col.category);
                        text = col.text;
                        subcategory = null;

                        // Add category divider if category changes
                        if(category != current_category) {
                            this.columns.splice(i, 0, {
                                value: "DIVIDER-"+ category,
                                category: category,
                                text: "",
                                subcategory: null
                            }); 
                            current_subcategory = null; // Reset subcategory when category changes
                            i++; // Increment i since we added an element
                        }
                    } else if (col.category && col.category.trim() != '') {
                        // Explicit category provided – use it before dot-based parsing
                        category = this.propercase(col.category);
                        text = col.text;

                        // Derive subcategory from either explicit property or dot-notation in value
                        if (col.subcategory && col.subcategory.trim() !== '') {
                            subcategory = this.propercase(col.subcategory);
                        } else if (colValue.indexOf(".") !== -1) {
                            const parts = colValue.split(".");
                            if (parts.length > 2) {
                                subcategory = this.propercase(parts[1]);
                            }
                        } else {
                            subcategory = null;
                        }

                        // Add category divider if category changes
                        if(category != current_category) {
                            this.columns.splice(i, 0, {
                                value: "DIVIDER-"+ category,
                                category: category,
                                text: "",
                                subcategory: null
                            });
                            current_subcategory = null; // Reset subcategory when category changes
                            i++; // Increment i since we added an element
                        }

                        // Add subcategory divider if subcategory exists and changes
                        if(subcategory && subcategory != current_subcategory) {
                            this.columns.splice(i, 0, {
                                value: "DIVIDER-"+ category + "-" + subcategory,
                                category: category,
                                subcategory: subcategory,
                                text: ""
                            });
                            i++; // Increment i since we added an element
                        }
                    } else if(colValue.indexOf(".") != -1) {
                        let parts = colValue.split(".");
                        category = this.propercase(parts[0]);
                        
                        if(parts.length > 2) {
                            subcategory = this.propercase(parts[1]);
                            // For 3+ levels, join remaining parts with ›
                            text = this.propercase(parts.slice(2).join(' › '));
                        } else {
                            text = this.propercase(parts[1]);
                        }

                        // Add category divider if category changes
                        if(category != current_category) {
                            this.columns.splice(i, 0, {
                                value: "DIVIDER-"+ category,
                                category: category,
                                text: "",
                                subcategory: null
                            });
                            current_subcategory = null; // Reset subcategory when category changes
                            i++; // Increment i since we added an element
                        }

                        // Add subcategory divider if subcategory changes
                        if(subcategory && subcategory != current_subcategory) {
                            this.columns.splice(i, 0, {
                                value: "DIVIDER-"+ category + "-" + subcategory,
                                category: category,
                                subcategory: subcategory,
                                text: ""
                            });
                            i++; // Increment i since we added an element
                        }
                    }

                    current_category = category;
                    current_subcategory = subcategory;

                    // Update the column with the processed values
                    this.columns[i] = {
                        ...col,
                        category: category,
                        subcategory: subcategory,
                        text: text
                    };
                }

                // Sort each level by text, but keep headers with their options
                this.columns.sort((a, b) => {
                    // First sort by category
                    if (a.category !== b.category) {
                        if (!a.category && b.category) return -1;
                        if (a.category && !b.category) return 1;
                        if (a.category && b.category) {
                            return this.compareValues(a.category, b.category);
                        }
                    }

                    // If same category, sort by subcategory
                    if (a.subcategory !== b.subcategory) {
                        if (!a.subcategory && b.subcategory) return -1;
                        if (a.subcategory && !b.subcategory) return 1;
                        if (a.subcategory && b.subcategory) {
                            return this.compareValues(a.subcategory, b.subcategory);
                        }
                    }

                    // If same category and subcategory, sort by text
                    // But handle headers specially - headers should come before their options
                    const aText = a.text || '';
                    const bText = b.text || '';
                    const aIsHeader = aText === '';
                    const bIsHeader = bText === '';

                    // If one is header and one is not, headers should come first
                    if (aIsHeader && !bIsHeader) return -1;
                    if (!aIsHeader && bIsHeader) return 1;

                    // If both are headers or both are options, sort normally
                    return this.compareValues(aText, bText);
                });
            },
            expandCategory(cat, expand) {
                if (!cat) return false;
                
                const isExpanded = this.expanded_categories.includes(cat);
                const shouldExpand = expand === true || !isExpanded;

                // Update state arrays immediately for reactive updates
                if (shouldExpand && !this.expanded_categories.includes(cat)) {
                    this.expanded_categories.push(cat);
                } else if (!shouldExpand) {
                    this.expanded_categories = this.expanded_categories.filter(c => c !== cat);
                    // Remove all subcategories of this category
                    this.expanded_subcategories = this.expanded_subcategories.filter(sc => !sc.startsWith(cat + '-'));
                }

                // Use $nextTick to ensure DOM updates happen after Vue's reactive updates
                this.$nextTick(() => {
                    // Find all elements related to this category
                    const categoryHeader = document.querySelector(`div[data-category='${cat}']`);
                    if (!categoryHeader) return;

                    // Update category header
                    const categoryIcon = categoryHeader.querySelector('.fa-caret-right, .fa-caret-down');
                    if (categoryIcon) {
                        categoryIcon.classList.toggle('fa-caret-right', !shouldExpand);
                        categoryIcon.classList.toggle('fa-caret-down', shouldExpand);
                    }
                    categoryHeader.dataset.isexpanded = shouldExpand;

                    // Find and update all related subcategory headers
                    const subcategoryHeaders = document.querySelectorAll(`div[data-subcategory='${cat}'].subcategory-header`);
                    subcategoryHeaders.forEach(header => {
                        header.style.display = shouldExpand ? "block" : "none";
                        if (!shouldExpand) {
                            header.dataset.isexpanded = "false";
                            const subcategoryIcon = header.querySelector('.fa-caret-right, .fa-caret-down');
                            if (subcategoryIcon) {
                                subcategoryIcon.classList.add('fa-caret-right');
                                subcategoryIcon.classList.remove('fa-caret-down');
                            }
                        }
                    });
                });

                return false;
            },
            expandSubcategory(cat, subcat, expand) {
                if (!cat || !subcat) return false;

                const subcategoryKey = `${cat}-${subcat}`;
                const isExpanded = this.expanded_subcategories.includes(subcategoryKey);
                const shouldExpand = expand === true || !isExpanded;

                // Update state array immediately for reactive updates
                if (shouldExpand && !this.expanded_subcategories.includes(subcategoryKey)) {
                    this.expanded_subcategories.push(subcategoryKey);
                } else if (!shouldExpand) {
                    this.expanded_subcategories = this.expanded_subcategories.filter(sc => sc !== subcategoryKey);
                }

                // Use $nextTick to ensure DOM updates happen after Vue's reactive updates
                this.$nextTick(() => {
                    const header = document.querySelector(`div[data-subcategory='${cat}'][data-subcategory-name='${subcat}']`);
                    if (!header) return;

                    // Update header
                    const icon = header.querySelector('.fa-caret-right, .fa-caret-down');
                    if (icon) {
                        icon.classList.toggle('fa-caret-right', !shouldExpand);
                        icon.classList.toggle('fa-caret-down', shouldExpand);
                    }
                    header.dataset.isexpanded = shouldExpand;

                    // Find and update all options under this subcategory
                    const options = document.querySelectorAll(`div[data-subcategory='${cat}'][data-subcategory-name='${subcat}'] + div.suboption`);
                    options.forEach(option => {
                        option.style.display = shouldExpand ? "block" : "none";
                    });
                });

                return false;
            },
            shouldShowOption(category, subcategory) {
                let classes = [];
                
                if (!category) {
                    classes.push('show');
                } else {
                    classes.push('category-option');
                    const categoryExpanded = this.expanded_categories.includes(category);
                    if (!categoryExpanded) return classes.join(' ');
                    
                    if (!subcategory) {
                        classes.push('show');
                    } else {
                        classes.push('subcategory-option');
                        const subcategoryKey = `${category}-${subcategory}`;
                        if (this.expanded_subcategories.includes(subcategoryKey)) {
                            classes.push('show');
                        }
                    }
                }
                
                return classes.join(' ');
            },
            handleHover(event) {
                // Remove selected and highlight classes from all options
                const allOptions = document.querySelectorAll('.vs__dropdown-option');
                allOptions.forEach(option => {
                    option.classList.remove('vs__dropdown-option--selected', 'vs__dropdown-option--highlight');
                });
            },
            fetchOptions(search, loading) {
                //Reset the array
                this.columns = JSON.parse(JSON.stringify(this.orig_columns));

                if(search == "") {
                    // Reset expanded states when no search
                    this.expanded_categories = [];
                    this.expanded_subcategories = [];
                    return;
                }

                //Look at each column
                for(var i = this.columns.length-1; i >= 0 ; i--) {
                    const item = this.columns[i];
                    
                    // Check if search matches text, category, or subcategory
                    const searchLower = search.toLowerCase();
                    const textMatch = item.text && item.text.toLowerCase().indexOf(searchLower) !== -1;
                    const categoryMatch = item.category && item.category.toLowerCase().indexOf(searchLower) !== -1;
                    const subcategoryMatch = item.subcategory && item.subcategory.toLowerCase().indexOf(searchLower) !== -1;
                    
                    // Keep item if it matches search in any field, or if it's a category/subcategory divider
                    if (!textMatch && !categoryMatch && !subcategoryMatch && item.text !== "") {
                        this.columns.splice(i, 1);
                    }
                }

                //Get the remaining categories and subcategories
                var cats = [];
                var subcats = new Map(); // Map to track subcategories and their matching options
                
                for(var i = 0; i < this.columns.length; i++) {
                    const item = this.columns[i];
                    if(item.category != null && item.text != "") {
                        if(!cats.includes(item.category)) {
                            cats.push(item.category);
                        }
                        
                        // Track subcategories that have matching options
                        if(item.subcategory) {
                            const key = `${item.category}-${item.subcategory}`;
                            if(!subcats.has(key)) {
                                subcats.set(key, false);
                            }
                            subcats.set(key, true);
                        }
                    }
                }

                //Remove subcategory headers that have no matching options
                for(var i = this.columns.length-1; i >= 0 ; i--) {
                    const item = this.columns[i];
                    if(item.text == "" && item.subcategory) {
                        const key = `${item.category}-${item.subcategory}`;
                        if(!subcats.get(key)) {
                            this.columns.splice(i, 1);
                        }
                    }
                }

                //Remove category headers that have no matching options
                for(var i = this.columns.length-1; i >= 0 ; i--) {
                    const item = this.columns[i];
                    if(item.text == "" && item.category != null && !cats.includes(item.category)) {
                        this.columns.splice(i, 1);
                    }
                }

                //Expand the categories and subcategories
                for(var i = 0; i < cats.length; i++) {
                    this.expandCategory(cats[i], true);
                    // Find and expand all subcategories for this category
                    subcats.forEach((hasMatches, key) => {
                        const [category, subcategory] = key.split('-');
                        if (category === cats[i] && hasMatches) {
                            this.expandSubcategory(category, subcategory, true);
                        }
                    });
                }
                return true;
            },
            initTooltips() {
                this.$nextTick(() => {
                    try {
                        const tooltipTriggerList = this.$el.querySelectorAll('[data-bs-toggle="tooltip"]');
                        [...tooltipTriggerList].forEach(tooltipTriggerEl => {
                            try {
                                // Check if Bootstrap is available
                                if (!window.bootstrap || !window.bootstrap.Tooltip) {
                                    console.warn('Bootstrap Tooltip is not available');
                                    return;
                                }

                                // Dispose any existing tooltip
                                const existingTooltip = window.bootstrap.Tooltip.getInstance(tooltipTriggerEl);
                                if (existingTooltip) {
                                    existingTooltip.dispose();
                                }

                                // Only create new tooltip if element is valid
                                if (tooltipTriggerEl && tooltipTriggerEl.getAttribute('data-bs-original-title')) {
                                    new window.bootstrap.Tooltip(tooltipTriggerEl, {
                                        trigger: 'hover',
                                        html: true,
                                        container: 'body'
                                    });
                                }
                            } catch (tooltipError) {
                                console.warn('Error initializing tooltip:', tooltipError);
                            }
                        });
                    } catch (error) {
                        console.warn('Error in initTooltips:', error);
                    }
                });
            },
            processOptions(options) {
                this.columns = [];
                this.orig_columns = [];
                
                // Handle string options
                if (typeof options === 'string') {
                    this.columns = [];
                    this.orig_columns = [];
                    return;
                }
                
                // Handle non-array options
                if (!Array.isArray(options)) {
                    console.warn('AccordionSelect: options prop must be an array or string');
                    this.columns = [];
                    this.orig_columns = [];
                    return;
                }
                
                for(var i = 0; i < options.length; i++) {
                    // Skip filtering for non-string values
                    if (typeof options[i].value === 'string') {
                        //Don't include any columns that start with _
                        if((options[i].value.indexOf("_") == 0 && options[i].value.indexOf("__") != 0) || (options[i].value.indexOf("._") >= 0 && options[i].value.indexOf(".__") == -1))
                            continue;
                    }
                    this.columns.push(options[i]);
                }
                this.addHeaders();
                this.orig_columns = JSON.parse(JSON.stringify(this.columns));

                // Ensure value_obj is set correctly
                this.$nextTick(() => {
                    this.resetValueObject();
                    this.initTooltips();
                });
            },
            isSubcategoryVisible(category, subcategory) {
                // On initial load, subcategory should only be visible if its parent category is expanded
                // If the category is not expanded, hide all its subcategories
                return this.expanded_categories.includes(category);
            },
            getDisplayText(text) {
                if (!text) return '';
                
                // If splitText is disabled, return the text as-is
                if (!this.splitText) {
                    return text;
                }
                
                // Try different possible separators
                const separators = [' › ', ' > ', ' - ', ': '];
                for (const separator of separators) {
                    if (text.includes(separator)) {
                        const parts = text.split(separator);
                        return parts.pop();
                    }
                }
                
                return text;
            },
        }
    }
</script>
